import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import plusIcon from '../../assets/img/icons/plus_white.svg';
import CreateProjectDropdown from '../../components/DropdownStyle/CreateProjectDropdown';
import Select from '../../components/ReactSelectDropdown';
import { filterOption } from '../../components/ReactSelectDropdown/ReactSelectDropdown';
import {
  ButtonIcon,
  ColoredButtonWithIcon,
} from '../../styledComponents/buttons';
import {
  FilterLabel,
  HeaderContainer,
  InputField,
  InputWithLabelSection,
  PageTitle,
} from '../../styledComponents/common';
import { CommonGrid } from '../../styledComponents/common';
import AddGeofenceLocationModal from './AddGeofenceLocationModal';
import GeofenceLocationsTable from './GeofenceLocationsTable';
import StaticTaskLocationModal from 'custom_modules/TaskLocations/StaticTaskLocationModal';

const GeofenceLocations = props => {
  const { t } = useTranslation();
  const userRole = localStorage.getItem('user_role');

  // Redirect non-admin users
  useEffect(() => {
    if (userRole !== 'admin') {
      // Redirect to home or show access denied
      window.location.href = '/user/dashboard';
      // Or use React Router navigation if available
      // navigate('/user/dashboard');
    }
  }, [userRole]);

  // If not admin, don't render the component
  if (userRole !== 'admin') {
    return null;
  }

  const {
    getGeofenceLocations,
    deleteGeofenceLocation,
    addGeofenceLocation,
    geofenceLocations,
    geofenceLocationsLoading,
    addGeofenceLocationLoading,
    updateGeofenceLocation,
    updateGeofenceLocationLoading,
  } = props;

  const [taskLocations, setTaskLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
  const [seeLocationModalOpen, setSeeLocationModalOpen] = useState(false);
  const [selectedTaskLocation, setSelectedTaskLocation] = useState(null);
  const [taskLocationUpdate, setTaskLocationUpdate] = useState(false);
  const [selectedLocationType, setSelectedLocationType] = useState(null);
  const [locationTypeOptions, setLocationTypeOptions] = useState([]);

  const getLocationTypeList = () => {
    setLocationTypeOptions([
      {
        label: t('all'),
        value: null,
      },
      {
        label: t('circle'),
        value: 'circle',
      },
      {
        label: t('polygon'),
        value: 'polygon',
      },
    ]);
  };

  useEffect(() => {
    // Debounce the search to avoid too many API calls
    const delayDebounceFn = setTimeout(() => {
      const params = {};

      // Add search term to params if it exists
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      // Add location type to params if it's selected
      if (selectedLocationType?.value) {
        if (selectedLocationType.value === 'circle') {
          params.type = 'circular';
        } else if (selectedLocationType.value === 'polygon') {
          params.type = 'polygonal';
        }
      }

      // Call the API with params
      // call only if searchTerm or selectedLocationType changes
      if (searchTerm || selectedLocationType) {
        getGeofenceLocations(params);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedLocationType]);

  const selectLocationType = (e) => {
    setSelectedLocationType(e);
  };

  useEffect(() => {

    getLocationTypeList();

    setSelectedLocationType(locationTypeOptions[0]);

    // Initial load without params
    getGeofenceLocations();
  }, []);

  useEffect(() => {
    if (geofenceLocations && geofenceLocations.length > 0) {
      setTaskLocations(geofenceLocations);
    } else if (geofenceLocations && geofenceLocations.length === 0) {
      setTaskLocations([]);
    }
  }, [geofenceLocations]);

  const onSearchTermChange = e => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  const handleShowLocation = location => {
    setSelectedTaskLocation(location);

    setTimeout(() => {
      setSeeLocationModalOpen(true);
    }, 300);
  };

  const handleEditLocation = location => {
    setTaskLocationUpdate(true);
    setSelectedTaskLocation(location);
    setAddLocationModalOpen(true);
  };

  const handleDeleteLocation = (location) => {
    deleteGeofenceLocation(location.id);
  };


  return (
    <div className="content">
      <PageTitle>{t('geofence_locations')}</PageTitle>
      <HeaderContainer
        columns={
          localStorage.getItem('user_role') === 'admin'
            ? '500px auto'
            : '250px auto'
        }
      >
        <CommonGrid
          columns="180px 250px 250px"
          gap="15px"
          justifyContent="flex-start"
        >
          <InputWithLabelSection>
            <FilterLabel>{t('search_location')}</FilterLabel>
            <InputField
              type="text"
              placeholder={t('search_by_location_name')}
              value={searchTerm}
              onChange={e => onSearchTermChange(e)}
            />
          </InputWithLabelSection>
          <InputWithLabelSection>
            <FilterLabel>{t('select_geofence_location_type')}</FilterLabel>

            <Select
              isSearchable
              value={selectedLocationType}
              options={locationTypeOptions}
              placeholder={t('select_geofence_location_type')}
              onChange={e => {
                selectLocationType(e);
              }}
              styles={CreateProjectDropdown()}
              // clearable
              filterOption={filterOption}
            />
          </InputWithLabelSection>
        </CommonGrid>
        {localStorage.getItem('viewer_role') !== 'management' && (
          <ColoredButtonWithIcon onClick={() => {
            setTaskLocationUpdate(false);
            setSelectedTaskLocation(null);
            setAddLocationModalOpen(true);
          }}>
            <ButtonIcon src={plusIcon} alt="" />
            {t('add_new_location')}
          </ColoredButtonWithIcon>
        )}
      </HeaderContainer>
      <AddGeofenceLocationModal
        isOpen={addLocationModalOpen}
        toggle={() => {
          setAddLocationModalOpen(false);
          setTaskLocationUpdate(false);
          setSelectedTaskLocation(null);
        }}
        addGeofenceLocation={addGeofenceLocation}
        addGeofenceLocationLoading={addGeofenceLocationLoading}
        editData={taskLocationUpdate ? selectedTaskLocation : null}
        taskLocation={selectedTaskLocation}
        taskLocationUpdate={taskLocationUpdate}
        updateGeofenceLocation={updateGeofenceLocation}
        updateGeofenceLocationLoading={updateGeofenceLocationLoading}
      />
      <StaticTaskLocationModal
        isOpen={seeLocationModalOpen}
        toggle={() => {
          setSeeLocationModalOpen(false);
          setSelectedTaskLocation(null);
        }}
        taskLocation={selectedTaskLocation}
      />
      <GeofenceLocationsTable
        locations={taskLocations}
        loading={geofenceLocationsLoading}
        handleShowLocation={handleShowLocation}
        onEdit={handleEditLocation}
        onDelete={handleDeleteLocation}
        searchText={searchTerm}
        onSearch={onSearchTermChange}
        selectedLocationType={selectedLocationType}
      />
    </div>
  );
};

export default GeofenceLocations;
